import { nextContactSaveChanges } from "./nextContactSave.mjs";
import { normalizeNextContactDate, saoPauloDateKey } from "./futureFollowUpGuard.mjs";
import { prospectWriteWasStale } from "./prospectItemWrites.mjs";

export async function saveNextContactOnExit(record, date, { kind, update, addReminder, reminderId }) {
  if (!update || !addReminder) throw new Error("next_contact_persistence_unavailable");
  const dueDate = normalizeNextContactDate(date);
  const changes = nextContactSaveChanges(record, dueDate, new Date().toISOString());
  if (kind === "lead") changes.proxima_data_contato = dueDate;
  const written = await update({ ...record, ...changes }, changes);
  if (written?.ok === false || written?.stale === true || written?.sessionExpired
    || (kind === "prosp" && prospectWriteWasStale(written))) {
    throw new Error("next_contact_card_failed");
  }
  const linkedField = kind === "lead" ? "leadId" : kind === "prosp" ? "prospId" : "clientId";
  const result = await addReminder({
    id:reminderId, [linkedField]:String(record.id),
    storeId:Number(record.lojaAtual || record.storeId),
    consultantId:record.consultantId ?? record.consultorAtual ?? null,
    text:"Próximo contato", clientName:record.nome || record.name || "",
    dueDate, status:"pending", createdAt:saoPauloDateKey(), doneAt:null, type:"followup",
  });
  if (result?.ok !== true) throw new Error("next_contact_reminder_failed");
  return { ...record, ...changes };
}

export async function retryNextContactSave(save, date, context) {
  let lastError;
  for (let attempt = 0; attempt < 3; attempt++) {
    let timer;
    try {
      return await Promise.race([
        Promise.resolve().then(() => save(date, context)),
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("next_contact_timeout")), 4000); }),
      ]);
    }
    catch (error) {
      lastError = error;
      if (attempt < 2) await new Promise(resolve => setTimeout(resolve, 250 * (attempt + 1)));
    }
    finally { clearTimeout(timer); }
  }
  throw lastError;
}

export function reportNextContactBlock(context, reason) {
  const screen = ["lead", "prospeccao", "central"].includes(context?.screen) ? context.screen : "central";
  const storeId = Number(context?.storeId);
  if (![1, 2, 3, 4].includes(storeId)) return;
  const event = {
    screen, storeId, reason, timestamp:new Date().toISOString(),
    eventId:`evt-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`,
  };
  // Deliberately no record ID, name, phone, owner, date draft or error text.
  try { localStorage.setItem(BLOCK_QUEUE, JSON.stringify([...readBlockQueue(), event])); } catch {}
  void flushNextContactBlocks(event);
}

const BLOCK_QUEUE = "arflow_next_contact_blocks_pending";
let flushingBlocks = false;
function readBlockQueue() {
  try {
    const rows = JSON.parse(localStorage.getItem(BLOCK_QUEUE) || "[]");
    return Array.isArray(rows) ? rows : [];
  } catch { return []; }
}
export async function flushNextContactBlocks(fallback) {
  if (flushingBlocks || typeof fetch !== "function") return;
  flushingBlocks = true;
  try {
    let queue = readBlockQueue();
    if (!queue.length && fallback) queue = [fallback];
    for (const event of queue) {
      const response = await fetch("/api/crm/next-contact-blocks", {
        method:"POST", credentials:"include", keepalive:true,
        headers:{ "Content-Type":"application/json" }, body:JSON.stringify(event),
      });
      if (!response.ok) break;
      try { localStorage.setItem(BLOCK_QUEUE, JSON.stringify(readBlockQueue().filter(item => item.eventId !== event.eventId))); } catch {}
    }
  } catch {} finally { flushingBlocks = false; }
}
